import { useState } from 'react';

/**
 * Custom hook for managing approval functionality
 * @returns {Object} - Approval state and functions
 */
export const useApproval = () => {
  const [approvalModal, setApprovalModal] = useState({
    isOpen: false,
    itemData: null,
    itemType: null,
    onApprovalSent: null
  });

  /**
   * Open the approval modal
   * @param {Object} itemData - The item data that needs approval
   * @param {string} itemType - Type of item ('booking', 'catalog', 'tour', 'flight', 'hotel', 'transport')
   * @param {Function} onApprovalSent - Callback when approval is sent successfully
   */
  const openApprovalModal = (itemData, itemType, onApprovalSent) => {
    setApprovalModal({
      isOpen: true,
      itemData,
      itemType,
      onApprovalSent
    });
  };

  /**
   * Close the approval modal
   */
  const closeApprovalModal = () => {
    setApprovalModal({
      isOpen: false,
      itemData: null,
      itemType: null,
      onApprovalSent: null
    });
  };

  /**
   * Quick function to send booking for approval
   * @param {Object} bookingData - Booking data
   * @param {Function} onSuccess - Success callback
   */
  const sendBookingForApproval = (bookingData, onSuccess) => {
    openApprovalModal(bookingData, 'booking', onSuccess);
  };

  /**
   * Quick function to send catalog item for approval
   * @param {Object} catalogData - Catalog data
   * @param {Function} onSuccess - Success callback
   */
  const sendCatalogForApproval = (catalogData, onSuccess) => {
    openApprovalModal(catalogData, 'catalog', onSuccess);
  };

  /**
   * Quick function to send flight for approval
   * @param {Object} flightData - Flight data
   * @param {Function} onSuccess - Success callback
   */
  const sendFlightForApproval = (flightData, onSuccess) => {
    openApprovalModal(flightData, 'flight', onSuccess);
  };

  /**
   * Quick function to send hotel for approval
   * @param {Object} hotelData - Hotel data
   * @param {Function} onSuccess - Success callback
   */
  const sendHotelForApproval = (hotelData, onSuccess) => {
    openApprovalModal(hotelData, 'hotel', onSuccess);
  };

  /**
   * Quick function to send transport for approval
   * @param {Object} transportData - Transport data
   * @param {Function} onSuccess - Success callback
   */
  const sendTransportForApproval = (transportData, onSuccess) => {
    openApprovalModal(transportData, 'transport', onSuccess);
  };

  /**
   * Quick function to send tour item for approval (generic tour items)
   * @param {Object} tourData - Tour data
   * @param {Function} onSuccess - Success callback
   */
  const sendTourForApproval = (tourData, onSuccess) => {
    // Determine the specific tour item type from the data
    let itemType = 'tour'; // default
    
    if (tourData.departure && tourData.arrival) {
      itemType = 'flight';
    } else if (tourData.hotelName || tourData.name) {
      itemType = 'hotel';
    } else if (tourData.type && (tourData.pickup || tourData.destination)) {
      itemType = 'transport';
    }
    
    openApprovalModal(tourData, itemType, onSuccess);
  };

  return {
    // Modal state
    approvalModal,
    
    // Core functions
    openApprovalModal,
    closeApprovalModal,
    
    // Quick functions for specific types
    sendBookingForApproval,
    sendCatalogForApproval,
    sendFlightForApproval,
    sendHotelForApproval,
    sendTransportForApproval,
    sendTourForApproval
  };
};
